import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { io, type Socket } from 'socket.io-client'
import { api, socketOrigin } from '@/api/client'

export type NotificationKind =
  | 'shared_meal'
  | 'shared_activity'
  | 'shared_event'
  | 'plan_ready'
  | 'plan_failed'
  | 'sync_failed'
  | 'share_granted'
  | 'share_revoked'
  | 'household'

export interface Notification {
  id: string
  kind: NotificationKind
  title: string
  body: string | null
  actorMemberId: string | null
  actorName: string | null
  subjectKind: string | null
  subjectId: string | null
  payload: Record<string, unknown>
  /** Null on a statement. A question is 'pending' until it is answered. */
  decision: 'pending' | 'accepted' | 'rejected' | null
  decidedAt: string | null
  readAt: string | null
  createdAt: string
}

/**
 * One inbox, holding two kinds of thing: statements, and questions somebody
 * is waiting on an answer to.
 *
 * New ones arrive over socket.io on a room only this person is in. The list
 * is still the record — a blocked websocket means a slower inbox, not an
 * empty one — so every action reloads the counts rather than trusting the
 * local arithmetic.
 */
export const useNotificationsStore = defineStore('notifications', () => {
  const list = ref<Notification[]>([])
  const unread = ref(0)
  const pending = ref(0)
  const loading = ref(false)
  const error = ref<string | null>(null)
  let socket: Socket | null = null

  const questions = computed(() => list.value.filter((n) => n.decision === 'pending'))

  async function load(filter: 'all' | 'unread' | 'pending' = 'all') {
    loading.value = true
    error.value = null
    try {
      list.value = await api.get<Notification[]>('/notifications', { filter })
      await refreshCounts()
    } catch (err) {
      error.value = (err as Error).message
    } finally {
      loading.value = false
    }
  }

  async function refreshCounts() {
    const counts = await api.get<{ unread: number; pending: number }>('/notifications/counts')
    unread.value = counts.unread
    pending.value = counts.pending
  }

  async function markRead(id: string) {
    const item = list.value.find((n) => n.id === id)
    if (!item || item.readAt) return
    await api.patch(`/notifications/${id}/read`)
    item.readAt = new Date().toISOString()
    await refreshCounts()
  }

  async function markAllRead() {
    await api.post('/notifications/read-all')
    const now = new Date().toISOString()
    list.value = list.value.map((n) => ({ ...n, readAt: n.readAt ?? now }))
    await refreshCounts()
  }

  async function decide(id: string, decision: 'accepted' | 'rejected', servings?: number | null) {
    const updated = await api.post<Notification>(`/notifications/${id}/decision`, {
      decision,
      servings: servings ?? null,
    })
    list.value = list.value.map((n) => (n.id === id ? updated : n))
    await refreshCounts()
    return updated
  }

  /** Share the socket's shape with the jobs store, but not its room. */
  function connect(accessToken: string) {
    if (socket) return
    // The API's origin where there is one, the page's otherwise. Under
    // Capacitor the page's origin is the APK, and a socket aimed there
    // retries against the asset loader forever.
    const url = socketOrigin()
    socket = url
      ? io(url, { path: '/socket.io', auth: { token: accessToken } })
      : io({ path: '/socket.io', auth: { token: accessToken } })
    socket.on('notification', (incoming: Notification) => {
      if (list.value.some((n) => n.id === incoming.id)) return
      list.value = [incoming, ...list.value]
      unread.value += 1
      if (incoming.decision === 'pending') pending.value += 1
    })
  }

  function disconnect() {
    socket?.disconnect()
    socket = null
    list.value = []
    unread.value = 0
    pending.value = 0
  }

  return {
    list,
    questions,
    unread,
    pending,
    loading,
    error,
    load,
    refreshCounts,
    markRead,
    markAllRead,
    decide,
    connect,
    disconnect,
  }
})
